import { useEffect } from "react";
import { Link } from "react-router-dom";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import { toStarRating } from "@b2bportal/core-utilities";
import {
  type HotelItinerary,
  ReservationStateEnum,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import { useHotelBookingSupportId } from "../../hooks";
import { ResendConfirmationEmail } from "../ResendConfirmationEmail";
import styles from "./styles.module.scss";
import { ActionButton } from "@/components/ActionButton";
import { ContactSupportDialogContent } from "@/components/ContactSupportDialog";
import { Rewards } from "@/components/Rewards";
import { TRIPS_CANCEL_HOTEL_ROUTE } from "@/pages/Trips";
import { getLodgingAddress } from "@/utils/lodging";
import { calculatePointsEarnedFromItinerary } from "@/utils/points";

export const HotelReservationPageHeader = ({
  itinerary,
}: {
  itinerary: HotelItinerary;
}) => {
  const reservation = itinerary.reservation;
  const [, cn] = useModuleBEM(styles, "HotelReservationPageHeader");
  const { t } = useI18nContext();
  const { supportId, isLoading, fetchBookingSupportId, error } =
    useHotelBookingSupportId();

  useEffect(() => {
    fetchBookingSupportId(reservation.reservationId);
  }, [reservation.reservationId, fetchBookingSupportId]);

  const hotelData = reservation.lodgingData;
  if (hotelData == null) {
    return null;
  }

  return (
    <div className={cn("container")}>
      <div className={cn("back-button-container")}>
        <Link to={TRIPS_PATH} className={cn("back-button")}>
          <Icon
            iconName={IconNameEnum.backIcon}
            className={cn("back-button-icon")}
          />{" "}
          {t("hotels.bookingConfirmation.back")}
        </Link>
      </div>
      <h1 className={cn("title")}>{hotelData.name}</h1>
      <div className={cn("product-description")}>
        <div className={cn("star-rating")}>
          <Icon iconName={IconNameEnum.starFilled} />{" "}
          {toStarRating(hotelData.starRating)}
        </div>
        <div className={cn("address")}>
          <Icon iconName={IconNameEnum.mapPin} /> {getLodgingAddress(hotelData)}
        </div>
      </div>
      <div className={cn("image-container")}>
        <img src={hotelData.media[0]?.url} alt={hotelData.name} />
      </div>
      <div className={cn("pre-content")}>
        <div className={cn("reservation-management")}>
          <ResendConfirmationEmail
            buttonClassName={cn("manage-button")}
            itinerary={itinerary}
          />
          {itinerary.reservation.state.ReservationState !==
          ReservationStateEnum.Cancelled ? (
            <Link
              className={cn("manage-button")}
              to={TRIPS_CANCEL_HOTEL_ROUTE.replace(
                ":reservationId",
                reservation.reservationBookingId
              )}
            >
              {t("hotels.bookingConfirmation.cancel")}
            </Link>
          ) : null}
          <Dialog>
            <DialogTrigger className={cn("manage-button")}>
              {t("hotels.bookingConfirmation.contactSupport")}
            </DialogTrigger>
            <DialogContent
              className={cn("contact-support-dialog")}
              title={t("hotels.bookingConfirmation.contactSupportDialog.title")}
              bottomFixedContent={
                <DialogClose>
                  <ActionButton
                    message={t(
                      "hotels.bookingConfirmation.contactSupportDialog.closeButton"
                    )}
                  />
                </DialogClose>
              }
            >
              <ContactSupportDialogContent
                showBookingQuestion={true}
                supportId={supportId}
              />
              <div className={cn("priority-id-section")}>
                <div className={cn("priority-id-label")}>
                  {t(
                    "hotels.bookingConfirmation.contactSupportDialog.priorityIdLabel"
                  )}
                </div>
                <div className={cn("priority-id-value")}>
                  {isLoading
                    ? t(
                        "hotels.bookingConfirmation.contactSupportDialog.loadingPriorityId"
                      )
                    : supportId}
                  {!isLoading && !supportId && error && (
                    <div className={cn("error-message")}>
                      {t(
                        "hotels.bookingConfirmation.contactSupportDialog.priorityIdError"
                      )}
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className={cn("rewards-container")}>
          <Rewards
            amount={calculatePointsEarnedFromItinerary(itinerary)}
            className={cn("rewards")}
            messageKey="hotels.bookingConfirmation.rewardsSection.earnedPointsLabel"
          />
        </div>
      </div>
    </div>
  );
};
